import { useCallback, useMemo, useReducer } from 'react';

/** Most undo steps kept; older ones are dropped. */
const LIMIT = 100;
/** Edits with the same coalesce key closer together than this become one step (e.g. typing). */
const COALESCE_MS = 800;

export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  /** The value when a gesture (e.g. a canvas drag) began; edits during it are one step. */
  gesture: { start: T } | null;
  lastKey: string | null;
  lastAt: number;
}

export type HistoryAction<T> =
  | { type: 'set'; update: (prev: T) => T; key?: string; now: number }
  | { type: 'replace'; value: T }
  | { type: 'reset'; value: T }
  | { type: 'begin' }
  | { type: 'end' }
  | { type: 'undo' }
  | { type: 'redo' };

export function initialHistory<T>(present: T): HistoryState<T> {
  return { past: [], present, future: [], gesture: null, lastKey: null, lastAt: 0 };
}

const push = <T>(past: T[], value: T): T[] => [...past, value].slice(-LIMIT);

/** Pure undo/redo reducer over immutable snapshots. */
export function historyReducer<T>(state: HistoryState<T>, action: HistoryAction<T>): HistoryState<T> {
  switch (action.type) {
    case 'set': {
      const next = action.update(state.present);
      if (Object.is(next, state.present)) return state;
      if (state.gesture) return { ...state, present: next };
      const coalesce =
        action.key !== undefined && action.key === state.lastKey && action.now - state.lastAt < COALESCE_MS;
      return {
        ...state,
        past: coalesce ? state.past : push(state.past, state.present),
        present: next,
        future: [],
        lastKey: action.key ?? null,
        lastAt: action.now,
      };
    }
    case 'replace':
      return { ...state, present: action.value };
    case 'reset':
      return initialHistory(action.value);
    case 'begin':
      return state.gesture ? state : { ...state, gesture: { start: state.present }, lastKey: null };
    case 'end': {
      if (!state.gesture) return state;
      const { start } = state.gesture;
      if (Object.is(start, state.present)) return { ...state, gesture: null };
      return { ...state, gesture: null, past: push(state.past, start), future: [] };
    }
    case 'undo': {
      const prev = state.past[state.past.length - 1];
      if (state.gesture || prev === undefined) return state;
      return {
        ...state,
        past: state.past.slice(0, -1),
        present: prev,
        future: [state.present, ...state.future],
        lastKey: null,
      };
    }
    case 'redo': {
      const [next, ...rest] = state.future;
      if (state.gesture || next === undefined) return state;
      return { ...state, past: push(state.past, state.present), present: next, future: rest, lastKey: null };
    }
  }
}

export interface History<T> {
  value: T;
  /** Records an edit. Edits sharing `key` within a short window merge into one undo step. */
  set: (update: (prev: T) => T, key?: string) => void;
  /** Swaps the current value without an undo step (e.g. the server's copy after a save). */
  replace: (value: T) => void;
  /** Starts over with no history (e.g. a freshly loaded document). */
  reset: (value: T) => void;
  /** Brackets a gesture so all edits between the two count as one step. */
  begin: () => void;
  end: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useHistory<T>(initial: T): History<T> {
  const [state, dispatch] = useReducer(historyReducer<T>, initial, initialHistory);
  const set = useCallback(
    (update: (prev: T) => T, key?: string) => dispatch({ type: 'set', update, key, now: Date.now() }),
    [],
  );
  const actions = useMemo(
    () => ({
      replace: (value: T) => dispatch({ type: 'replace', value }),
      reset: (value: T) => dispatch({ type: 'reset', value }),
      begin: () => dispatch({ type: 'begin' }),
      end: () => dispatch({ type: 'end' }),
      undo: () => dispatch({ type: 'undo' }),
      redo: () => dispatch({ type: 'redo' }),
    }),
    [],
  );
  return {
    value: state.present,
    set,
    ...actions,
    canUndo: state.past.length > 0 && !state.gesture,
    canRedo: state.future.length > 0 && !state.gesture,
  };
}
